import React, {
    useState,
    forwardRef,
    useEffect,
    Fragment,
} from "react";
import {
    Stepper,
    Step,
    StepLabel,
    Typography,
    Button,
} from "@material-ui/core";
import {
    addQueryInitialData,
    cargoFuelPriceStep,
    expenseResultField,
    fuelPriceOptions,
    loadlineOptions,
    portEmissionOptions,
    termsCoEfficientTable,
} from "../../../constants";
import * as actions from "./actions";
import {
    getCurrentUserOrganizationId,
    toastSuccess,
} from "../../../utils";
import moment from "moment";
import { useDispatch } from "react-redux";
import * as commonActions from "../../Common/actions";
import { validateNewQuery } from "./addQueryValidation";
import { setCopyPortOperation, setCopyQuery } from "./dataMapping";
import { handleKeyDown } from "../../../common/common";
import { useParams } from "react-router-dom";
import CustomConfirmModal from "../../../components/Common/CustomConfirmModal";

function getSteps() {
    return ['Fuel Prices', 'Revenue & expenses', "Extra Cargo details"];
}

const CreateNewCargo = forwardRef((props, ref) => {
    const {
        setLoader = () => { },
        onCloseModal = () => { },
        selectedQuery = {},
        selectedPorts = [],
        selectedPortsCommence = [],
        isEdit = false,
    } = props;

    const organizationId = getCurrentUserOrganizationId();

    const addPortInitialData = [
        {
            cargoId: "", operation: "Loading", port: "", cargoQty: 0, tolerance: 0, rate: 0, terms: "SHINC", turnTime: 0, pdaRemarks: "", grade: "", freight: 0, adcom: 0, other: 0, brokerage: 0, ttlComm: 0, netFrt: 0, min: 0, max: 0, account: "", cargo: "", factor: 1, stowFactor: 0, queryId: "", loadline: "S-Summer", sulfur: "Normal",
        },
        {
            cargoId: "", operation: "Discharging", port: "", cargoQty: 0, tolerance: 0, rate: 0, terms: "SHINC", turnTime: 0, pdaRemarks: "", grade: "", freight: 0, adcom: 0, other: 0, brokerage: 0, ttlComm: 0, netFrt: 0, min: 0, max: 0, account: "", cargo: "", factor: 1, stowFactor: 0, queryId: "", loadline: "S-Summer", sulfur: "Normal",
        },
    ];

    const dispatch = useDispatch();

    const [portOperations, setPortOperations] = useState([...addPortInitialData]);
    const [query, setQuery] = useState({ ...addQueryInitialData });
    const [activeStep, setActiveStep] = useState(0);
    const [warningPopup, setWarningPopup] = useState(false);
    const steps = getSteps();

    useEffect(() => {
        if (Object.keys(selectedQuery).length) {
            setQuery(selectedQuery);
        }
        if (Array.isArray(selectedPorts) && selectedPorts.length) {
            setPortOperations(selectedPorts);
        }
        return () => resetStateVariables();
    }, [selectedQuery, selectedPorts]);

    useEffect(() => {
        const { errors } = validateNewQuery(query, portOperations);
        dispatch(commonActions.addOnlyErrors({ errorList: errors }));
    }, [query, portOperations]);

    const resetStateVariables = () => {
        setPortOperations([...addPortInitialData]);
        setQuery({ ...addQueryInitialData });
    };

    const onOpenErrorAlert = (errors = []) => {
        dispatch(commonActions.openErrorAlert({ errorList: errors }));
    };

    const onSubmitQuery = async (isCopy = false) => {
        const { errors, isValid } = validateNewQuery(query, portOperations);
        if (!isValid) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1)
            return onOpenErrorAlert(errors);
        }
        setLoader(true);
        const finalPortOperations = [...selectedPortsCommence, ...portOperations]

        let newQuery = {
            ...query,
            isAllStepClear: true,
            isShowInCalculator: true,
            laycanStartDate: moment(query.laycanStartDate).toISOString(),
            laycanEndDate: moment(query.laycanEndDate).toISOString(),
        };
        let newPortOperation = isCopy ? setCopyPortOperation(finalPortOperations) : finalPortOperations;
        if (isCopy) newQuery = setCopyQuery(newQuery);
        newQuery.cargoList = newPortOperation;

        const formData = new FormData();
        formData.append('QueryData', JSON.stringify(newQuery) || '');

        await actions.updateClientQuery(formData).then((res) => {
            toastSuccess("Query updated successfully");
            window.dispatchEvent(new CustomEvent("guide:cargo-matrix"));
            onCloseModal();
            Promise.all([
                !!organizationId ? dispatch(actions.clientQueryOrg()) : null,
                dispatch(actions.clientQueryUser()),
                dispatch(actions.getQueryFormatForUser()),
            ]);
            setLoader(false);
        }).catch(() => setLoader(false));
        setWarningPopup(false);
    }

    const onChangePortOperation = (index, e) => {
        const { name = "", value = "", type = "" } = e.target;
        let updatedPortOperation = [...portOperations];
        updatedPortOperation[index][name] = type === "number" ? Number(value) : value;
        if (name === "terms") {
            const findFactor = termsCoEfficientTable.find((item) => item.label === value);
            if (findFactor) updatedPortOperation[index]["factor"] = findFactor.value;
        }
        setPortOperations(updatedPortOperation);
    };

    const {
        miscRevenue = 0,
        hsFuelPriceType0 = 0, lsFuelPriceType0 = 0, mgoFuelPriceType0 = 0,
        hsFuelPriceType1 = 0, lsFuelPriceType1 = 0, mgoFuelPriceType1 = 0,
        hsFuelPriceType2 = 0, lsFuelPriceType2 = 0, mgoFuelPriceType2 = 0,
        hsFuelPriceType3 = 0, lsFuelPriceType3 = 0, mgoFuelPriceType3 = 0,
        hsFuelPriceType4 = 0, lsFuelPriceType4 = 0, mgoFuelPriceType4 = 0,
    } = query || {};

    let fuelTypePrices = {
        TYPE0: { normal: hsFuelPriceType0 || 0, eca: lsFuelPriceType0 || 0, mgo: mgoFuelPriceType0 || 0 },
        TYPE1: { normal: hsFuelPriceType1 || 0, eca: lsFuelPriceType1 || 0, mgo: mgoFuelPriceType1 || 0 },
        TYPE2: { normal: hsFuelPriceType2 || 0, eca: lsFuelPriceType2 || 0, mgo: mgoFuelPriceType2 || 0 },
        TYPE3: { normal: hsFuelPriceType3 || 0, eca: lsFuelPriceType3 || 0, mgo: mgoFuelPriceType3 || 0 },
        TYPE4: { normal: hsFuelPriceType4 || 0, eca: lsFuelPriceType4 || 0, mgo: mgoFuelPriceType4 || 0 },
    };

    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);
    const handleNext = () => {
        if (activeStep !== 2) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            if (isEdit) {
                setWarningPopup(true);
            } else {
                onSubmitQuery(false);
            }
        }
    };
    const handleReset = () => setActiveStep(0);

    const onChangeExpenses = (e) => {
        const { value = 0, name = "" } = e.target || {};
        setQuery({ ...query, [name]: Number(value) });
    }

    const onChangeFuelPrice = (e, fuelType) => {
        const { name = "", value = "" } = e.target;
        let newClientQuery = { ...query };
        const currentType = fuelType.replace("TYPE", "");
        if (name === "normal") newClientQuery[`hsFuelPriceType${currentType}`] = parseFloat(value);
        if (name === "eca") newClientQuery[`lsFuelPriceType${currentType}`] = parseFloat(value);
        if (name === "mgo") newClientQuery[`mgoFuelPriceType${currentType}`] = parseFloat(value);
        setQuery(newClientQuery)
    };

    // Grid classes match the Cargo Operations grid (Orders/AddNewOrder.js)
    const thClass = "px-2 py-1 text-left border-r border-b border-slate-300 text-[11px] font-medium text-slate-800 bg-slate-100 capitalize tracking-wider align-middle";
    const tdClass = "border border-slate-300 p-0 align-middle bg-white";
    const labelTdClass = "border border-slate-300 px-2 align-middle bg-[#f0f2f5] text-[11px] font-medium text-slate-900";
    const cellInputClass = "w-full h-[22px] text-[11px] px-2 py-0 m-0 box-border block outline-none bg-[#ffffd6] text-slate-900 focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 rounded-none border-none font-medium tabular-nums [&[type=number]]:text-right";
    const sectionTitleClass = "text-[11px] font-medium text-slate-900 capitalize tracking-wider leading-none py-0.5 border-l-4 border-l-[#313C4C] pl-2 m-0";
    const labelClasses = "block text-[11px] font-medium text-slate-900 mb-1 capitalize tracking-wider";
    const notesClass = "bg-[#f8fafc] border border-slate-200 p-2";
    const noteTextClass = "list-disc pl-4 text-[11px] text-slate-600 space-y-0.5 m-0";

    function getStepContent(stepIndex) {
        switch (stepIndex) {
            case 0:
                return (
                    <div className="flex flex-col md:flex-row gap-4 px-1 py-2">
                        <div className="w-full md:w-6/12 space-y-3">
                            <div className={notesClass}>
                                <h4 className={labelClasses}>Notes</h4>
                                <ul className={noteTextClass}>
                                    {cargoFuelPriceStep?.notes?.map((itm, idx) => <li key={idx}>{itm}</li>)}
                                </ul>

                                <h4 className={`${labelClasses} mt-3`}>Comments</h4>
                                <ul className={noteTextClass}>
                                    {cargoFuelPriceStep?.comments?.map((itm, idx) => <li key={idx}>{itm}</li>)}
                                </ul>
                            </div>
                        </div>

                        <div className="w-full md:w-6/12 flex flex-col gap-2">
                            <h4 className={sectionTitleClass}>Fuel Prices Configuration</h4>
                            <table data-spotlight="wizard-fuel-prices" className="w-full text-left border-collapse border border-slate-300 table-fixed">
                                <thead>
                                    <tr>
                                        <th className={`${thClass} w-[25%]`}>Type</th>
                                        <th className={thClass}>Main</th>
                                        <th className={thClass}>ECA</th>
                                        <th className={thClass}>MGO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fuelPriceOptions.map((item, i) => (
                                        <tr key={i}>
                                            <td className={labelTdClass}>{item.label}</td>
                                            {["normal", "eca", "mgo"].map((type) => (
                                                <td key={type} className={tdClass}>
                                                    <input
                                                        type="number"
                                                        name={type}
                                                        placeholder="0"
                                                        onKeyDown={handleKeyDown}
                                                        onChange={(e) => onChangeFuelPrice(e, item?.value)}
                                                        value={fuelTypePrices?.[item?.value]?.[type]}
                                                        className={cellInputClass}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="flex flex-col md:flex-row gap-4 px-1 py-2">
                        <div className="w-full md:w-6/12">
                            <div className={notesClass}>
                                <h4 className={labelClasses}>Notes</h4>
                                <p className="text-[11px] text-slate-600 leading-snug m-0">
                                    <span className="text-slate-900">Revenue:</span> All lumpsum revenue including lumpsum freight, or any other, may be entered here.
                                </p>
                            </div>
                        </div>

                        <div data-spotlight="wizard-revenue-expenses" className="w-full md:w-6/12 flex flex-col gap-4">
                            <div className="flex flex-col gap-2">
                                <h4 className={sectionTitleClass}>Revenue</h4>
                                <table className="w-full text-left border-collapse border border-slate-300 table-fixed">
                                    <thead>
                                        <tr>
                                            <th className={thClass}>Item</th>
                                            <th className={`${thClass} w-[140px]`}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className={labelTdClass}>Misc Revenue</td>
                                            <td className={tdClass}>
                                                <input
                                                    type="number"
                                                    name="miscRevenue"
                                                    placeholder="0"
                                                    onKeyDown={handleKeyDown}
                                                    onChange={onChangeExpenses}
                                                    value={miscRevenue || ""}
                                                    className={cellInputClass}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex flex-col gap-2">
                                <h4 className={sectionTitleClass}>Expenses</h4>
                                <table className="w-full text-left border-collapse border border-slate-300 table-fixed">
                                    <thead>
                                        <tr>
                                            <th className={thClass}>Item</th>
                                            <th className={`${thClass} w-[140px]`}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenseResultField.map((item, key) => (
                                            <tr key={key}>
                                                <td className={labelTdClass}>{item.label}</td>
                                                <td className={tdClass}>
                                                    <input
                                                        type="number"
                                                        name={item.value}
                                                        placeholder="0"
                                                        onKeyDown={handleKeyDown}
                                                        onChange={onChangeExpenses}
                                                        value={query[item.value] || ""}
                                                        className={cellInputClass}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="w-full px-1 py-2 flex flex-col gap-3">
                        <h4 className={sectionTitleClass}>Port Operations</h4>

                        <div data-spotlight="wizard-port-ops" className="overflow-x-auto w-full">
                            <table className="w-full text-left border-collapse border border-slate-300 whitespace-nowrap table-fixed">
                                <thead>
                                    <tr>
                                        {['Operation', 'Port', 'Extra Days', 'Port Emission', 'Loadline', 'Draft Restriction', 'Water Density'].map((head) => (
                                            <th key={head} className={thClass}>{head}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {portOperations.map((item, index) => {
                                        const { operation = "", port = "", extraDays = "", waterDensity = "", draftRes } = item || {};
                                        return (
                                            <tr key={index}>
                                                <td className={labelTdClass}>{operation}</td>
                                                <td className={`${labelTdClass} text-slate-600`}>{port || "-"}</td>
                                                <td className={tdClass}>
                                                    <input type="number" onKeyDown={handleKeyDown} name="extraDays"
                                                        value={extraDays} onChange={(e) => onChangePortOperation(index, e)}
                                                        className={cellInputClass} />
                                                </td>
                                                <td className={tdClass}>
                                                    <select name="sulfur" value={item.sulfur || "Normal"}
                                                        onChange={(e) => onChangePortOperation(index, e)}
                                                        className={`${cellInputClass} cursor-pointer`}>
                                                        {portEmissionOptions.map((opt, i) => <option key={i} value={opt.label}>{opt.label}</option>)}
                                                    </select>
                                                </td>
                                                <td className={tdClass}>
                                                    <select name="loadline" value={item.loadline || "loadline"}
                                                        onChange={(e) => onChangePortOperation(index, e)}
                                                        className={`${cellInputClass} cursor-pointer`}>
                                                        {loadlineOptions.map((opt, i) => <option key={i} value={opt.label}>{opt.label}</option>)}
                                                    </select>
                                                </td>
                                                <td className={tdClass}>
                                                    <input type="number" onKeyDown={handleKeyDown} name="draftRes"
                                                        value={draftRes} onChange={(e) => onChangePortOperation(index, e)}
                                                        className={cellInputClass} />
                                                </td>
                                                <td className={tdClass}>
                                                    <input type="number" onKeyDown={handleKeyDown} name="waterDensity"
                                                        value={waterDensity} onChange={(e) => onChangePortOperation(index, e)}
                                                        className={cellInputClass} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={notesClass}>
                            <h4 className={labelClasses}>Notes</h4>
                            <ul className={noteTextClass}>
                                <li>
                                    Port Emission - Normal will burn Main Fuel, and Port Emission - ECA will burn ECA Fuel.
                                </li>
                                <li>
                                    Our system automatically applies the maximum cargo loadable quantity based on loadline, draft restrictions, and water density. If you wish to load less cargo, reduce the cargo quantity or increase the constants to adjust cargo loadability.
                                </li>
                            </ul>
                        </div>
                    </div>
                );
            default: return null;
        }
    }

    return (
        <Fragment>
            <div className="w-full bg-[#ffffff] p-3 rounded-lg font-sans">
                {/* ORIGINAL MATERIAL-UI STEPPER */}
                <Stepper activeStep={activeStep} className="stepper-background" style={{ background: "#ffffff", padding: "8px 0" }} alternativeLabel>
                    {steps.map((label) => (
                        <Step style={{ background: "#ffffff" }} key={label}>
                            <StepLabel style={{ background: "#ffffff" }}>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <div className="mt-3 mb-4 min-h-[240px]">
                    {activeStep === steps.length ? (
                        <div className="flex flex-col items-center justify-center p-8 bg-[#f8fafc] border border-[#e2e8f0] rounded-md text-center">
                            <Typography style={{ color: '#1e293b', fontWeight: 600, fontSize: '14px', marginBottom: '12px' }}>All steps completed</Typography>
                            <Button onClick={handleReset} variant="outlined" style={{ color: '#313C4C', borderColor: '#313C4C', textTransform: 'none', fontWeight: 600, padding: '4px 16px', fontSize: '12px' }}>
                                Reset
                            </Button>
                        </div>
                    ) : (
                        getStepContent(activeStep)
                    )}
                </div>

                {/* Modern Action Buttons - Compact */}
                {activeStep !== steps.length && (
                    <div className="flex justify-end gap-2 pt-3 border-t border-[#e2e8f0]">
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                            style={{
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '11px',
                                borderRadius: 0,
                                color: activeStep === 0 ? '#94a3b8' : '#313C4C',
                                borderColor: activeStep === 0 ? '#e2e8f0' : '#313C4C',
                                padding: '4px 16px'
                            }}
                        >
                            Back
                        </Button>
                        <Button
                            data-spotlight="wizard-next-btn"
                            onClick={handleNext}
                            variant="contained"
                            style={{
                                backgroundColor: '#313C4C',
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '11px',
                                borderRadius: 0,
                                padding: '4px 20px',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                        </Button>
                    </div>
                )}
            </div>

            <CustomConfirmModal
                open={warningPopup}
                handleClose={() => setWarningPopup(false)}
                onSubmitConfirm={() => onSubmitQuery(false)}
                title="Confirmation"
                message="Are you sure you want to show this order details in matrix page?"
                note="Please double check all the details before showing the order in matrix page. The changes will be reflected in the matrix page."
                cancelText="Cancel"
                confirmText="Confirm"
                type="info"
                maxWidth="md"
            />
        </Fragment>
    );
});

export default CreateNewCargo;